// =====================================================================
// Salary & Tax Declaration — self-service page for every role except
// admin. Opened from the sidebar "Payroll & Tax Summary" item. Three
// tabs:
//   1. Salary Declaration   — CTC + effective date + component split
//                             (Basic ≥ 40% CTC, HRA ≥ 50% Basic floors).
//   2. Investment Declaration — tax regime + deduction fields (80C, etc).
//   3. Earnings             — read-only computed breakdown (gross, EPF,
//                             ESIC, PT, LWF, TDS, net, CTC).
//
// Persistence reuses the existing employee get/update API — the current
// user's employee record is resolved by user_id (same pattern as
// Documents.tsx) and saved back via updateEmployee.
// =====================================================================

import React, { useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../../components/ui/tabs";
import {
  Wallet,
  PiggyBank,
  Calculator,
  Info,
  Save, 
  Loader2,
  Home,
  X,
} from "lucide-react";
import { useAuth } from "../../contexts/AuthContext";
import { useToast } from "../../hooks/useToast";
import {
  getEmployees,
  getEmployeeById,
  updateEmployee,
} from "../../api/employees";
import { getCompanyComplianceSettings } from "../../api/officeandcompany";
import {
  calcTDS,
  calcTaxableIncome,
  calcPT,
  lwfMonthly,
  fmt,
  num,
  salaryFloors,
  calcHraExemption,
} from "../../utils/employee.constants";

type SalaryRecord = {
  effectiveDate: string;
  basicSalary: number;
  hra: number;
  conveyance: number;
  medicalallowance: number;
  allowance: number;
  lta: number;
  others: number;
  bonusAmount: number;
  customComponents: { id: string; name: string; amount: number }[];
};

type Declaration = {
  sec80c: number;
  sec80tta: number;
  sec80d: number;
  sec80g: number;
  sec80eea: number;
  sec80ccd1b: number; // Employee NPS — 80CCD
  npsEmployer: number; // Employer NPS — 80CCD(2)
  otherDed: number;
  hraExempt: number;
  homeLoan: number;
  ltaExempt: number;
  ptPaid: number;
};

const EMPTY_RECORD = (): SalaryRecord => ({
  effectiveDate: new Date().toISOString().split("T")[0],
  basicSalary: 0,
  hra: 0,
  conveyance: 0,
  medicalallowance: 0,
  allowance: 0,
  lta: 0,
  others: 0,
  bonusAmount: 0,
  customComponents: [],
});

const EMPTY_DECLARATION = (): Declaration => ({
  sec80c: 0,
  sec80tta: 0,
  sec80d: 0,
  sec80g: 0,
  sec80eea: 0,
  sec80ccd1b: 0,
  npsEmployer: 0,
  otherDed: 0,
  hraExempt: 0,
  homeLoan: 0,
  ltaExempt: 0,
  ptPaid: 0,
});

export default function SalaryTaxDeclaration() {
  const { user } = useAuth();
  const { toast } = useToast();

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [employeeId, setEmployeeId] = useState<string | null>(null);
  const [employeeName, setEmployeeName] = useState("");

  const [ctcValue, setCtcValue] = useState(0); // annual or monthly per isYearly
  const [isYearly, setIsYearly] = useState(true);
  const [record, setRecord] = useState<SalaryRecord>(EMPTY_RECORD());
  const [declaration, setDeclaration] = useState<Declaration>(EMPTY_DECLARATION());
  const [taxRegime, setTaxRegime] = useState<"new" | "old">("new");
  const [tdsApplicable, setTdsApplicable] = useState(true);

  // Statutory config (defaults from company compliance, overridden by the
  // employee's own TaxConfig row when present).
  const [pfApplicable, setPfApplicable] = useState(true);
  const [pfBasis, setPfBasis] = useState<"statutory" | "actual">("statutory");
  // Employer PF is fixed at 12%. The employee may raise their own rate
  // (never below 12%). pfFromCtc: when true the employer's 12% is part of
  // CTC and reduces take-home.
  const [pfEmployeeRate, setPfEmployeeRate] = useState(12);
  const [pfFromCtc, setPfFromCtc] = useState(false);
  const [esicApplicable, setEsicApplicable] = useState(false);
  const [ptApplicable, setPtApplicable] = useState(true);
  const [ptState, setPtState] = useState("karnataka");
  const [lwfApplicable, setLwfApplicable] = useState(false);
  const [lwfState, setLwfState] = useState("karnataka");

  // Tax-computation breakdown is collapsed behind a button by default.
  const [showTaxBreakdown, setShowTaxBreakdown] = useState(false);

  // HRA rent-exemption calculator (old regime). Drives declaration.hraExempt.
  const [hraCalcOpen, setHraCalcOpen] = useState(false);
  const [paysRent, setPaysRent] = useState(true);
  const [annualRent, setAnnualRent] = useState(0);
  const [annualHraReceived, setAnnualHraReceived] = useState(0);
  const [isMetro, setIsMetro] = useState(true);

  const monthlyCtc = isYearly ? ctcValue / 12 : ctcValue;

  // ── Load: resolve current employee, then fetch full detail + company
  // compliance defaults and map into local state. ──────────────────────
  useEffect(() => {
    const load = async () => {
      setLoading(true);
      setLoadError(null);
      try {
        const truthy = (v: any) => v === true || v === 1 || v === "true";

        // 1. Find this user's employee record (same approach as Documents).
        const res = await getEmployees();
        const all = res.employees || [];
        const email = ((user as any)?.email || "").toString().toLowerCase();
        const me =
          all.find((e: any) => e.user_id === user?.id) ||
          (email
            ? all.find((e: any) => (e.email || "").toLowerCase() === email)
            : undefined);

        if (!me?.id) {
          setLoadError("Could not find your employee profile.");
          return;
        }
        setEmployeeId(me.id);

        // 2. Full detail (nested shapes tolerated, mirrors Documents.tsx).
        const detailRes: any = await getEmployeeById(me.id);
        const emp =
          detailRes?.employee?.employee ||
          detailRes?.employee ||
          detailRes ||
          {};
        setEmployeeName(
          `${emp.first_name || ""} ${emp.last_name || ""}`.trim() || me.name || "",
        );

        // 3. Company compliance defaults.
        let settings: any = null;
        try {
          const compliance = await getCompanyComplianceSettings();
          if (Array.isArray(compliance)) settings = compliance[0];
          else if (compliance?.data)
            settings = Array.isArray(compliance.data) ? compliance.data[0] : compliance.data;
          else settings = compliance;
        } catch {
          /* no company record yet → keep defaults */
        }
        settings = settings || {};

        // Per-employee TaxConfig row overrides company defaults when present.
        const tax = emp?.TaxConfig?.[0];
        setPfApplicable(truthy(tax ? tax.pfApplicable : settings.pfApplicable));
        setEsicApplicable(truthy(tax ? tax.esicApplicable : settings.esicApplicable));
        setPtApplicable(truthy(tax ? tax.ptApplicable : settings.ptApplicable));
        setLwfApplicable(truthy(tax ? tax.lwfApplicable : settings.lwfApplicable));
        setTdsApplicable(truthy(tax ? tax.tdsApplicable : settings.tdsApplicable));
        setPfBasis((emp.pfBasis || settings.pfBasis || "statutory") as any);
        setPtState(emp.ptState || settings.ptState || "karnataka");
        setLwfState(emp.lwfState || settings.lwfState || "karnataka");
        setTaxRegime((emp.taxRegime || settings.taxRegime || "new") as any);
        setPfFromCtc(truthy(settings.pfFromCtc));
        // Employee PF rate: the employee's own choice if set, else the
        // company default, never below 12%.
        setPfEmployeeRate(
          Math.max(12, num(emp.pfEmployeeRate) || num(settings.pfEmployeeRate) || 12),
        );

        // 4. Salary record (current = first).
        const sd = emp?.SalaryDetails?.[0];
        const rec: SalaryRecord = sd
          ? {
              effectiveDate:
                sd.effectiveDate || emp.hire_date?.split("T")[0] || EMPTY_RECORD().effectiveDate,
              basicSalary: num(sd.basicsalary),
              hra: num(sd.hra),
              conveyance: num(sd.conveyance),
              medicalallowance: num(sd.medicalallowance),
              allowance: num(sd.allowance),
              lta: num(sd.lta),
              others: num(sd.others),
              bonusAmount: num(sd.bonus),
              customComponents: sd.customComponents || [],
            }
          : { ...EMPTY_RECORD(), effectiveDate: emp.hire_date?.split("T")[0] || EMPTY_RECORD().effectiveDate };
        setRecord(rec);

        // 5. Declaration fields.
        setDeclaration({
          sec80c: num(emp.sec80c),
          sec80tta: num(emp.sec80tta),
          sec80d: num(emp.sec80d),
          sec80g: num(emp.sec80g),
          sec80eea: num(emp.sec80eea),
          sec80ccd1b: num(emp.sec80ccd1b),
          npsEmployer: num(emp.npsEmployer),
          otherDed: num(emp.otherDed),
          hraExempt: num(emp.hraExempt),
          homeLoan: num(emp.homeLoan),
          ltaExempt: num(emp.ltaExempt),
          ptPaid: num(emp.ptPaid),
        });

        // 6. Seed the CTC field from the loaded gross + employer costs.
        const gross =
          num(sd?.basicsalary) + num(sd?.hra) + num(sd?.conveyance) +
          num(sd?.medicalallowance) + num(sd?.allowance) + num(sd?.lta) +
          num(sd?.others) + num(sd?.bonus);
        if (gross > 0) {
          setCtcValue(Math.round(gross * 12));
          setIsYearly(true);
        }
      } catch (err: any) {
        setLoadError(err?.message || "Failed to load your salary details.");
      } finally {
        setLoading(false);
      }
    };
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id]);

  // ── Salary engine — identical formulas to EmployeeForm's
  // calculateSalaryDetailsForRecord, driven by the current statutory
  // config, declaration, and regime. ──────────────────────────────────
  const details = useMemo(() => {
    const basic = num(record.basicSalary);
    const hra = num(record.hra);
    const conv = num(record.conveyance);
    const med = num(record.medicalallowance);
    const special = num(record.allowance);
    const lta = num(record.lta);
    const other = num(record.others);
    const bonus = num(record.bonusAmount);
    const customTotal =
      record.customComponents?.reduce((s, c) => s + (c.amount || 0), 0) || 0;
    const gross = basic + hra + conv + med + special + lta + other + bonus + customTotal;

    const pfBase = pfBasis === "statutory" ? Math.min(basic, 15000) : basic;
    // Employer fixed 12%; employee can raise their rate (never below 12%).
    const pfEE = pfApplicable ? Math.round(pfBase * (pfEmployeeRate / 100)) : 0;
    const pfER = pfApplicable ? Math.round(pfBase * 0.12) : 0;
    const eps = pfApplicable ? Math.round(Math.min(pfBase, 15000) * 0.0833) : 0;
    const edli = pfApplicable ? Math.round(Math.min(pfBase, 15000) * 0.005) : 0;

    const esicEE = esicApplicable && gross <= 21000 ? Math.round(gross * 0.0075) : 0;
    const esicER = esicApplicable && gross <= 21000 ? Math.round(gross * 0.0325) : 0;

    const pt = ptApplicable ? calcPT(gross, ptState) : 0;

    const lwfData = lwfMonthly(lwfState);
    const lwfEE = lwfApplicable ? Math.round(lwfData.ee) : 0;
    const lwfER = lwfApplicable ? Math.round(lwfData.er) : 0;

    const grossAnnual = gross * 12;
    const pfEEAnnual = pfEE * 12;
    const pfERAnnual = pfER * 12;
    // Income tax is always computed on this tax-declaration page so the
    // employee always sees their real taxable income and tax. Both the
    // employee's and employer's PF reduce taxable income (company policy).
    const taxableAnnual = calcTaxableIncome(grossAnnual, taxRegime, declaration, pfEEAnnual, pfERAnnual);
    const tds = calcTDS(grossAnnual, taxRegime, declaration, pfEEAnnual, pfERAnnual);

    // Income tax + PT + LWF make up the monthly statutory tax. Employer PF
    // only reduces take-home when it's included in CTC.
    const employerPfInNet = pfFromCtc ? pfER : 0;
    const totDed = pfEE + esicEE + pt + lwfEE + tds + employerPfInNet;
    const net = gross - totDed;
    const ctc = gross + (pfFromCtc ? 0 : pfER) + eps + edli + esicER + lwfER;

    return {
      gross, pfEE, pfER, eps, edli, esicEE, esicER, pt, lwfEE, lwfER, tds,
      totDed, net, ctc, taxableAnnual, employerPfInNet,
    };
  }, [
    record, pfBasis, pfApplicable, pfEmployeeRate, pfFromCtc, esicApplicable,
    ptApplicable, ptState, lwfApplicable, lwfState, taxRegime, declaration,
  ]);

  const floors = salaryFloors(monthlyCtc, num(record.basicSalary));
  const basicBelowFloor = monthlyCtc > 0 && num(record.basicSalary) < floors.minBasic;

  // CTC conservation — the company-set CTC is fixed (read-only). As the
  // employee redistributes pay across components the total must always
  // equal the CTC: raising one component means lowering another. ctcDiff
  // > 0 → money still to allocate; < 0 → over-allocated (exceeds CTC).
  const monthlyCtcRounded = Math.round(monthlyCtc);
  const componentsTotal = Math.round(details.gross);
  const ctcDiff = monthlyCtcRounded - componentsTotal;
  const ctcBalanced = monthlyCtc <= 0 || Math.abs(ctcDiff) <= 1;
  const salaryValid = ctcBalanced && !basicBelowFloor;

  // ── Handlers ─────────────────────────────────────────────────────────
  const setField = (field: keyof SalaryRecord, value: number) =>
    setRecord((prev) => ({ ...prev, [field]: value }));

  const setDecl = (field: keyof Declaration, value: number) =>
    setDeclaration((prev) => ({ ...prev, [field]: value }));

  // Live preview of the HRA exemption from the calculator inputs.
  const hraExemptionPreview = paysRent
    ? calcHraExemption(
        num(record.basicSalary) * 12,
        annualHraReceived,
        annualRent,
        isMetro,
      )
    : 0;

  // Open the calculator, seeding "HRA received" from the current HRA
  // component (×12) and any previously-claimed exemption.
  const openHraCalc = () => {
    setAnnualHraReceived(num(record.hra) * 12);
    setHraCalcOpen(true);
  };

  const applyHraExemption = () => {
    setDecl("hraExempt", hraExemptionPreview);
    setHraCalcOpen(false);
  };

  const handleSave = async () => {
    if (!employeeId) return;
    if (pfApplicable && pfEmployeeRate < 12) {
      toast({
        title: "PF contribution too low",
        description: "Your PF contribution can't be below 12%.",
        variant: "destructive",
      });
      return;
    }
    if (!salaryValid) {
      toast({
        title: "Balance your salary components",
        description: !ctcBalanced
          ? `Your components total ₹${fmt(componentsTotal)} but your CTC is ₹${fmt(monthlyCtcRounded)}. Adjust a component so they match.`
          : `Basic must be ≥ ₹${fmt(floors.minBasic)} (50% of CTC).`,
        variant: "destructive",
      });
      return;
    }
    setSaving(true);
    try {
      const salaryDetail = {
        basicsalary: record.basicSalary,
        hra: record.hra,
        conveyance: record.conveyance,
        medicalallowance: record.medicalallowance,
        allowance: record.allowance,
        lta: record.lta,
        others: record.others,
        bonus: record.bonusAmount,
        pf: details.pfEE,
        esi: details.esicEE,
        proftax: details.pt,
        labourwelfare: details.lwfEE,
        tds: details.tds,
        netsalary: details.net,
        effectiveDate: record.effectiveDate,
        status: "active",
        customComponents: record.customComponents,
      };

      const payload: any = {
        id: employeeId,
        SalaryDetails: [salaryDetail],
        TaxConfig: [
          { pfApplicable, esicApplicable, ptApplicable, lwfApplicable, tdsApplicable },
        ],
        basicsalary: record.basicSalary,
        hra: record.hra,
        conveyance: record.conveyance,
        medicalallowance: record.medicalallowance,
        allowance: record.allowance,
        lta: record.lta,
        others: record.others,
        bonus: record.bonusAmount,
        netsalary: details.net,
        salary_amount: details.gross,
        tds: details.tds,
        pfApplicable,
        esicApplicable,
        ptApplicable,
        lwfApplicable,
        pfBasis,
        pfEmployeeRate,
        ptState,
        lwfState,
        taxRegime,
        ...declaration,
      };

      await updateEmployee(employeeId, payload);
      toast({
        title: "Declaration saved",
        description: "Your salary & tax declaration has been updated.",
      });
    } catch (err: any) {
      toast({
        title: "Couldn't save declaration",
        description:
          err?.response?.data?.detail || err?.message || "Please try again.",
        variant: "destructive",
      });
    } finally {
      setSaving(false);
    }
  };

  // ── Render ───────────────────────────────────────────────────────────
  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh] text-gray-500">
        <Loader2 className="w-5 h-5 animate-spin mr-2" /> Loading your salary details…
      </div>
    );
  }

  if (loadError) {
    return (
      <div className="max-w-xl mx-auto mt-10 p-6 bg-red-50 border border-red-200 rounded-lg text-center">
        <p className="font-semibold text-red-700 mb-1">{loadError}</p>
        <p className="text-sm text-red-500">
          Please contact HR if your payroll profile hasn't been set up yet.
        </p>
      </div>
    );
  }

  const regimeToggle = (
    <div className="inline-flex rounded-lg border border-gray-300 overflow-hidden">
      {(["new", "old"] as const).map((r) => (
        <button
          key={r}
          type="button"
          onClick={() => setTaxRegime(r)}
          className={`px-4 py-2 text-sm font-medium transition-colors ${
            taxRegime === r
              ? "bg-blue-600 text-white"
              : "bg-white text-gray-600 hover:bg-gray-50"
          }`}
        >
          {r === "new" ? "New Regime" : "Old Regime"}
        </button>
      ))}
    </div>
  );

  const componentField = (
    label: string,
    field: keyof SalaryRecord,
    opts?: { required?: boolean; help?: string; helpError?: boolean; extra?: React.ReactNode },
  ) => (
    <div>
      <Label className="text-xs font-semibold text-gray-500">
        {label} {opts?.required && <span className="text-red-500">*</span>}
      </Label>
      <Input
        type="number"
        value={(record[field] as number) === 0 ? "" : (record[field] as number)}
        onChange={(e) => setField(field, e.target.value === "" ? 0 : Number(e.target.value))}
        className={`mt-1 ${opts?.helpError ? "border-red-400 focus-visible:ring-red-400" : ""}`}
        placeholder="0"
      />
      {opts?.help && (
        <p className={`text-xs mt-1 ${opts.helpError ? "text-red-500" : "text-gray-400"}`}>
          {opts.help}
        </p>
      )}
      {opts?.extra}
    </div>
  );

  const deductionField = (
    label: string,
    field: keyof Declaration,
    tip: string,
  ) => (
    <div>
      <div className="flex items-center gap-1 mb-1">
        <Label className="text-sm font-medium text-gray-700">{label}</Label>
        <span title={tip} className="cursor-help text-gray-400">
          <Info className="w-3.5 h-3.5" />
        </span>
      </div>
      <div className="flex items-stretch rounded-lg border border-gray-300 overflow-hidden bg-white">
        <span className="px-3 flex items-center bg-gray-50 text-gray-500 border-r border-gray-200">₹</span>
        <input
          type="number"
          value={declaration[field] === 0 ? "" : declaration[field]}
          onChange={(e) => setDecl(field, e.target.value === "" ? 0 : Number(e.target.value))}
          placeholder="0"
          className="flex-1 px-3 py-2 outline-none text-sm"
        />
      </div>
    </div>
  );

  return (
    <div className="max-w-6xl mx-auto p-4 md:p-6 space-y-5">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Payroll & Tax Summary</h1>
          <p className="text-sm text-gray-500">
            {employeeName ? `${employeeName} · ` : ""}Declare your salary structure and tax-saving investments.
          </p>
        </div>
        <Button
          onClick={handleSave}
          disabled={saving || !salaryValid || (pfApplicable && pfEmployeeRate < 12)}
          title={!salaryValid ? "Balance your salary components before saving" : undefined}
          className="bg-blue-600 hover:bg-blue-700 text-white"
        >
          {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
          Save Declaration
        </Button>
      </div>

      <Tabs defaultValue="salary" className="w-full">
        <TabsList className="grid w-full grid-cols-3 h-auto p-1">
          <TabsTrigger value="salary" className="py-2.5">
            <Wallet className="w-4 h-4 mr-1.5" /> Salary
          </TabsTrigger>
          <TabsTrigger value="investment" className="py-2.5">
            <PiggyBank className="w-4 h-4 mr-1.5" /> Investment
          </TabsTrigger>
          <TabsTrigger value="earnings" className="py-2.5">
            <Calculator className="w-4 h-4 mr-1.5" /> Earnings
          </TabsTrigger>
        </TabsList>

        {/* ── Tab 1: Salary Declaration ─────────────────────────────── */}
        <TabsContent value="salary">
          <Card className="border shadow-sm">
            <CardHeader className="bg-gradient-to-r from-blue-50 to-indigo-50">
              <CardTitle className="text-lg">Salary Declaration</CardTitle>
              <CardDescription>
                Your CTC is set by the company and can't be changed. You can redistribute pay across
                components — Basic must stay ≥ 50% of CTC, and the components must always add up to
                your CTC.
              </CardDescription>
            </CardHeader>
            <CardContent className="p-6 space-y-5">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 bg-gray-50 p-4 rounded-lg border">
                <div>
                  <Label className="text-xs font-semibold text-gray-600 uppercase tracking-wide">
                    CTC <span className="text-gray-400 normal-case">(set by company)</span>
                  </Label>
                  <div className="flex gap-2 mt-1">
                    <Input
                      type="number"
                      value={ctcValue || ""}
                      readOnly
                      disabled
                      className="flex-1 bg-gray-100 text-gray-600 cursor-not-allowed"
                    />
                    <div className="w-24 flex items-center justify-center rounded-md border border-gray-200 bg-gray-100 text-sm text-gray-500">
                      {isYearly ? "Yearly" : "Monthly"}
                    </div>
                  </div>
                  {monthlyCtc > 0 && (
                    <p className="text-xs text-gray-500 mt-1">
                      Monthly CTC: ₹{fmt(monthlyCtc)} · Net pay: ₹{fmt(details.net)}
                    </p>
                  )}
                </div>
                <div>
                  <Label className="text-xs font-semibold text-gray-600 uppercase tracking-wide">
                    Effective Date <span className="text-gray-400 normal-case">(set by company)</span>
                  </Label>
                  <Input
                    type="date"
                    value={record.effectiveDate}
                    readOnly
                    disabled
                    className="mt-1 bg-gray-100 text-gray-600 cursor-not-allowed"
                  />
                </div>
                <div>
                  <Label className="text-xs font-semibold text-gray-600 uppercase tracking-wide">
                    Your PF Contribution (%) <span className="text-gray-400 normal-case">— min 12%</span>
                  </Label>
                  <Input
                    type="number"
                    step="0.1"
                    min={12}
                    value={pfEmployeeRate}
                    disabled={!pfApplicable}
                    onChange={(e) => setPfEmployeeRate(Number(e.target.value))}
                    onBlur={(e) => {
                      if (Number(e.target.value) < 12) setPfEmployeeRate(12);
                    }}
                    className="mt-1"
                  />
                  <p className={`text-xs mt-1 ${pfEmployeeRate < 12 ? "text-red-500" : "text-gray-400"}`}>
                    {pfEmployeeRate < 12
                      ? "Can't be below 12%."
                      : `Employer adds 12%${pfFromCtc ? " (included in CTC)" : " (over & above CTC)"}.`}
                  </p>
                </div>
              </div>

              {/* CTC conservation banner — total of components must equal CTC. */}
              {monthlyCtc > 0 && (
                <div
                  className={`flex items-center justify-between flex-wrap gap-2 rounded-lg border px-4 py-2.5 text-sm ${
                    ctcBalanced
                      ? "border-green-200 bg-green-50 text-green-700"
                      : "border-red-200 bg-red-50 text-red-700"
                  }`}
                >
                  <span>
                    Allocated <span className="font-semibold">₹{fmt(componentsTotal)}</span> of{" "}
                    <span className="font-semibold">₹{fmt(monthlyCtcRounded)}</span> monthly CTC
                  </span>
                  <span className="font-medium">
                    {ctcBalanced
                      ? "Balanced ✓"
                      : ctcDiff > 0
                      ? `₹${fmt(ctcDiff)} unallocated — increase another component`
                      : `Exceeds CTC by ₹${fmt(Math.abs(ctcDiff))} — reduce another component`}
                  </span>
                </div>
              )}

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {componentField("Basic Salary", "basicSalary", {
                  required: true,
                  help: basicBelowFloor
                    ? `Below minimum — Basic must be ≥ ₹${fmt(floors.minBasic)} (50% of CTC)`
                    : monthlyCtc > 0
                    ? `Min ₹${fmt(floors.minBasic)} (50% of CTC)`
                    : undefined,
                  helpError: basicBelowFloor,
                })}
                {componentField("HRA (House Rent Allowance)", "hra", {
                  extra: (
                    <button
                      type="button"
                      onClick={openHraCalc}
                      className="mt-1.5 inline-flex items-center gap-1 text-xs font-medium text-blue-600 hover:text-blue-700"
                    >
                      <Home className="w-3.5 h-3.5" />
                      {declaration.hraExempt > 0
                        ? `HRA exemption: ₹${fmt(declaration.hraExempt)}/yr — edit`
                        : "Claim HRA exemption (pay rent?)"}
                    </button>
                  ),
                })}
                {componentField("Conveyance Allowance", "conveyance")}
                {componentField("Medical Allowance", "medicalallowance")}
                {componentField("Special Allowance", "allowance")}
                {componentField("LTA (Leave Travel Allowance)", "lta")}
                {componentField("Other Allowances", "others")}
                {componentField("Bonus / Incentive", "bonusAmount")}
              </div>

              <div className="flex justify-end gap-4 text-sm pt-2 border-t">
                <span className="text-gray-500">
                  Gross / month:{" "}
                  <span className="font-bold text-gray-900">₹{fmt(details.gross)}</span>
                </span>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* ── Tab 2: Investment Declaration ─────────────────────────── */}
        <TabsContent value="investment">
          <Card className="border shadow-sm">
            <CardHeader className="bg-gradient-to-r from-emerald-50 to-green-50">
              <CardTitle className="text-lg">Investment Declaration</CardTitle>
              <CardDescription>
                Declare your tax-saving investments and deductions. Pick your tax regime in the
                Earnings tab — which of these apply depends on the regime you choose there.
              </CardDescription>
            </CardHeader>
            <CardContent className="p-6 space-y-5">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                {deductionField("Basic deductions – 80C", "sec80c", "PPF, ELSS, LIC, EPF, tuition fees, etc. Max ₹1,50,000.")}
                {deductionField("Interest from deposits – 80TTA", "sec80tta", "Interest from savings bank deposits. Max ₹10,000.")}
                {deductionField("Medical insurance – 80D", "sec80d", "Health insurance premiums for self & family. Max ₹50,000.")}
                {deductionField("Donations to charity – 80G", "sec80g", "Eligible donations to approved funds/charities.")}
                {deductionField("Interest on housing loan – 80EEA", "sec80eea", "Additional interest on first-time home loan. Max ₹1,50,000.")}
                {deductionField("Employee's contribution to NPS – 80CCD", "sec80ccd1b", "Self contribution to NPS. Max ₹50,000.")}
                {deductionField("Employer's contribution to NPS – 80CCD(2)", "npsEmployer", "Employer NPS contribution (monthly). Deductible in both regimes.")}
                {deductionField("Any other deduction", "otherDed", "Any other eligible deduction not listed above.")}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* ── Tab 3: Earnings ───────────────────────────────────────── */}
        <TabsContent value="earnings">
          <Card className="border shadow-sm">
            <CardHeader className="bg-gradient-to-r from-purple-50 to-pink-50">
              <CardTitle className="text-lg">Earnings & Tax Summary</CardTitle>
              <CardDescription>
                Computed from your salary components and investment declaration. Switch the tax regime to
                compare take-home pay.
              </CardDescription>
            </CardHeader>
            <CardContent className="p-6 space-y-5">
              <div className="flex items-center gap-3">
                <Label className="font-medium text-gray-700">Tax Regime:</Label>
                {regimeToggle}
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                <div className="rounded-lg border overflow-hidden">
                  <div className="px-4 py-2 bg-gray-50 font-semibold text-gray-700 text-sm">
                    Monthly Earnings
                  </div>
                  <div className="divide-y text-sm">
                    {[
                      ["Gross Salary", details.gross],
                      ["EPF (Employee)", -details.pfEE],
                      ...(pfFromCtc ? [["EPF (Employer, in CTC)", -details.employerPfInNet] as const] : []),
                      ["ESIC (Employee)", -details.esicEE],
                      ["Professional Tax", -details.pt],
                      ["Labour Welfare Fund", -details.lwfEE],
                      ["Income Tax (TDS)", -details.tds],
                    ].map(([label, val]) => (
                      <div key={label as string} className="flex justify-between px-4 py-2">
                        <span className="text-gray-600">{label}</span>
                        <span className={`font-medium ${(val as number) < 0 ? "text-red-600" : "text-gray-900"}`}>
                          {(val as number) < 0 ? "-" : ""}₹{fmt(Math.abs(val as number))}
                        </span>
                      </div>
                    ))}
                    <div className="flex justify-between px-4 py-3 bg-blue-50">
                      <span className="font-semibold text-blue-800">Net Pay (Take Home)</span>
                      <span className="font-bold text-blue-700">₹{fmt(details.net)}</span>
                    </div>
                  </div>
                </div>

                <div className="rounded-lg border overflow-hidden">
                  <div className="px-4 py-2 bg-gray-50 font-semibold text-gray-700 text-sm">
                    Employer Contributions & CTC
                  </div>
                  <div className="divide-y text-sm">
                    {[
                      ["EPF (Employer, 12%)", details.pfER],
                      ["EPS", details.eps],
                      ["EDLI", details.edli],
                      ["ESIC (Employer)", details.esicER],
                      ["LWF (Employer)", details.lwfER],
                    ].map(([label, val]) => (
                      <div key={label as string} className="flex justify-between px-4 py-2">
                        <span className="text-gray-600">{label}</span>
                        <span className="font-medium text-gray-900">₹{fmt(val as number)}</span>
                      </div>
                    ))}
                    <div className="flex justify-between px-4 py-2 bg-purple-50">
                      <span className="font-semibold text-purple-800">Monthly CTC</span>
                      <span className="font-bold text-purple-700">₹{fmt(details.ctc)}</span>
                    </div>
                    <div className="flex justify-between px-4 py-3 bg-purple-100">
                      <span className="font-semibold text-purple-900">Annual CTC</span>
                      <span className="font-bold text-purple-800">₹{fmt(details.ctc * 12)}</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Tax computation — collapsed behind a button by default. */}
              <button
                type="button"
                onClick={() => setShowTaxBreakdown((v) => !v)}
                className="w-full flex items-center justify-between px-4 py-3 rounded-lg border border-gray-300 bg-white hover:bg-gray-50 transition-colors text-sm font-semibold text-gray-700"
              >
                <span className="flex items-center gap-2">
                  <Calculator className="w-4 h-4 text-purple-600" />
                  {showTaxBreakdown ? "Hide" : "View"} tax computation
                </span>
                <span className="text-gray-400">{showTaxBreakdown ? "▲" : "▼"}</span>
              </button>

              {showTaxBreakdown && (() => {
                const grossAnnual = details.gross * 12;
                const empPfAnnual = details.pfEE * 12;
                const employerPfAnnual = details.pfER * 12;
                const stdDeduction = taxRegime === "new" ? 75000 : 50000;
                const otherDeductions = Math.max(
                  0,
                  grossAnnual - empPfAnnual - employerPfAnnual - stdDeduction - details.taxableAnnual,
                );
                const incomeTaxAnnual = details.tds * 12;
                const ptAnnual = details.pt * 12;
                const lwfAnnual = details.lwfEE * 12;
                const totalAnnualTax = incomeTaxAnnual + ptAnnual + lwfAnnual;
                const rows: [string, number, boolean?][] = [
                  ["Annual gross income", grossAnnual],
                  ["− Employee PF", -empPfAnnual],
                  ["− Employer PF", -employerPfAnnual],
                  [`− Standard deduction (${taxRegime === "new" ? "new" : "old"} regime)`, -stdDeduction],
                  ["− Other declared deductions", -otherDeductions],
                  ["= Taxable income", details.taxableAnnual, true],
                  ["Income tax + 4% cess (after rebate/relief)", incomeTaxAnnual],
                  ["+ Professional Tax", ptAnnual],
                  ["+ Labour Welfare Fund", lwfAnnual],
                  ["= Total annual tax", totalAnnualTax, true],
                ];
                return (
                  <div className="rounded-lg border overflow-hidden">
                    <div className="px-4 py-2 bg-gray-50 font-semibold text-gray-700 text-sm">
                      Tax computation ({taxRegime === "new" ? "New" : "Old"} Regime)
                    </div>
                    <div className="divide-y text-sm">
                      {rows.map(([label, val, strong]) => (
                        <div
                          key={label}
                          className={`flex justify-between px-4 py-2 ${strong ? "bg-gray-50 font-semibold text-gray-800" : ""}`}
                        >
                          <span className={strong ? "" : "text-gray-600"}>{label}</span>
                          <span className={val < 0 ? "text-red-600 font-medium" : "text-gray-900 font-medium"}>
                            {val < 0 ? "-" : ""}₹{fmt(Math.abs(val))}
                          </span>
                        </div>
                      ))}
                      <div className="flex justify-between px-4 py-3 bg-amber-50">
                        <span className="font-semibold text-amber-800">Monthly tax</span>
                        <span className="font-bold text-amber-700">
                          ₹{fmt(details.tds + details.pt + details.lwfEE)}
                        </span>
                      </div>
                    </div>
                  </div>
                );
              })()}

              {showTaxBreakdown && (
                <p className="text-xs text-gray-500">
                  If taxable income is ₹12,00,000 or below, income tax is zero (₹60,000 rebate); just
                  above ₹12,00,000 marginal relief caps the tax at the amount over ₹12,00,000.
                </p>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* ── HRA rent-exemption calculator (modal) ───────────────────── */}
      {hraCalcOpen &&
        createPortal(
          <div
            className="fixed inset-0 z-[1000] flex items-center justify-center p-4 bg-black/40"
            onClick={() => setHraCalcOpen(false)}
          >
            <div
              className="bg-white rounded-xl shadow-2xl w-full max-w-md"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-start justify-between gap-3 px-4 py-3 border-b">
                <div>
                  <h3 className="text-base font-bold text-gray-900">Do you live in a rented home?</h3>
                  <p className="text-xs text-gray-500 mt-0.5">
                    Pay rent? Claim an HRA benefit in the Old regime to save tax.
                  </p>
                </div>
                <button
                  onClick={() => setHraCalcOpen(false)}
                  className="p-1 text-gray-400 hover:text-gray-600 shrink-0"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>

              <div className="px-4 py-3 space-y-3">
                <div className="grid grid-cols-2 gap-2">
                  <button
                    type="button"
                    onClick={() => setPaysRent(true)}
                    className={`px-3 py-1.5 rounded-lg border text-xs font-semibold transition-colors ${
                      paysRent
                        ? "border-blue-500 bg-blue-50 text-blue-700"
                        : "border-gray-300 text-gray-600 hover:bg-gray-50"
                    }`}
                  >
                    Yes, I pay rent
                  </button>
                  <button
                    type="button"
                    onClick={() => setPaysRent(false)}
                    className={`px-3 py-1.5 rounded-lg border text-xs font-semibold transition-colors ${
                      !paysRent
                        ? "border-blue-500 bg-blue-50 text-blue-700"
                        : "border-gray-300 text-gray-600 hover:bg-gray-50"
                    }`}
                  >
                    No / I own my home
                  </button>
                </div>

                {paysRent && (
                  <>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      <div>
                        <Label className="text-xs font-semibold text-gray-700">Rent you pay (/year)</Label>
                        <div className="flex items-stretch rounded-lg border border-gray-300 overflow-hidden bg-white mt-1">
                          <span className="px-2.5 flex items-center bg-gray-50 text-gray-500 border-r border-gray-200 text-sm">₹</span>
                          <input
                            type="number"
                            value={annualRent || ""}
                            onChange={(e) => setAnnualRent(Number(e.target.value))}
                            placeholder="0"
                            className="flex-1 px-2.5 py-1.5 outline-none text-sm"
                          />
                        </div>
                        <p className="text-[11px] text-gray-400 mt-0.5">Monthly rent × 12.</p>
                      </div>

                      <div>
                        <Label className="text-xs font-semibold text-gray-700">HRA received (/year)</Label>
                        <div className="flex items-stretch rounded-lg border border-gray-300 overflow-hidden bg-white mt-1">
                          <span className="px-2.5 flex items-center bg-gray-50 text-gray-500 border-r border-gray-200 text-sm">₹</span>
                          <input
                            type="number"
                            value={annualHraReceived || ""}
                            onChange={(e) => setAnnualHraReceived(Number(e.target.value))}
                            placeholder="0"
                            className="flex-1 px-2.5 py-1.5 outline-none text-sm"
                          />
                        </div>
                        <p className="text-[11px] text-gray-400 mt-0.5">Payslip 'HRA' × 12.</p>
                      </div>
                    </div>

                    <div>
                      <Label className="text-xs font-semibold text-gray-700">Which city do you live in?</Label>
                      <div className="grid grid-cols-2 gap-2 mt-1">
                        <button
                          type="button"
                          onClick={() => setIsMetro(true)}
                          className={`px-3 py-1.5 rounded-lg border text-xs font-semibold transition-colors ${
                            isMetro
                              ? "border-blue-500 bg-blue-50 text-blue-700"
                              : "border-gray-300 text-gray-600 hover:bg-gray-50"
                          }`}
                        >
                          Metro city
                        </button>
                        <button
                          type="button"
                          onClick={() => setIsMetro(false)}
                          className={`px-3 py-1.5 rounded-lg border text-xs font-semibold transition-colors ${
                            !isMetro
                              ? "border-blue-500 bg-blue-50 text-blue-700"
                              : "border-gray-300 text-gray-600 hover:bg-gray-50"
                          }`}
                        >
                          Non-metro
                        </button>
                      </div>
                      <p className="text-[11px] text-gray-400 mt-0.5">
                        Metro = Delhi, Mumbai, Kolkata, Chennai (50% of Basic); others 40%.
                      </p>
                    </div>

                    <div className="rounded-lg bg-green-50 border border-green-200 px-3 py-2">
                      <div className="text-sm text-gray-800">
                        HRA tax-free amount:{" "}
                        <span className="font-bold text-green-700">₹{fmt(hraExemptionPreview)}</span> / year
                      </div>
                      <div className="text-[11px] text-gray-500">
                        Lowest of three legal limits, picked automatically.
                      </div>
                    </div>
                  </>
                )}
              </div>

              <div className="flex justify-end gap-2 px-4 py-3 border-t">
                <button
                  onClick={() => setHraCalcOpen(false)}
                  className="px-3 py-1.5 text-sm rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  onClick={applyHraExemption}
                  className="px-3 py-1.5 text-sm rounded-lg bg-blue-600 text-white hover:bg-blue-700"
                >
                  {paysRent ? "Apply exemption" : "Clear exemption"}
                </button>
              </div>
            </div>
          </div>,
          document.body,
        )}
    </div>
  );
}
